import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { staffApi } from "@client/api/staff";
import type { Result } from "@client/api/types";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Skeleton } from "@client/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/results/")({
  component: ResultsPage,
});

type SessionGroup = {
  sessionId: string;
  title: string;
  count: number;
  passed: number;
  passRate: number;
  avgScore: number;
  lastGraded: string;
};

function groupBySession(results: Result[]): SessionGroup[] {
  const map = new Map<string, Result[]>();
  for (const r of results) {
    const list = map.get(r.session_id);
    if (list) list.push(r);
    else map.set(r.session_id, [r]);
  }
  const groups: SessionGroup[] = [];
  for (const [sessionId, list] of map) {
    const first = list[0];
    if (!first) continue;
    const passed = list.filter((r) => r.passed).length;
    const avgScore =
      list.reduce((sum, r) => sum + Number(r.percentage), 0) / list.length;
    const lastGraded = list.reduce(
      (latest, r) => (r.graded_at > latest ? r.graded_at : latest),
      first.graded_at,
    );
    groups.push({
      sessionId,
      title: first.exam_sessions?.title ?? "Untitled session",
      count: list.length,
      passed,
      passRate: (passed / list.length) * 100,
      avgScore,
      lastGraded,
    });
  }
  // Most recently graded session first.
  return groups.sort((a, b) => b.lastGraded.localeCompare(a.lastGraded));
}

function ResultsPage() {
  const { data: results, isLoading } = useQuery({
    queryKey: ["results"],
    queryFn: staffApi.results,
  });

  const groups = useMemo(() => groupBySession(results ?? []), [results]);

  return (
    <StaffShell
      title="Results"
      subtitle="Visible to admins and instructors only"
    >
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="panel p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No graded sessions yet. Results appear here once a session is
            graded.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {groups.map((g) => (
            <SessionCard key={g.sessionId} group={g} />
          ))}
        </div>
      )}
    </StaffShell>
  );
}

function SessionCard({ group }: { group: SessionGroup }) {
  const pass = Math.round(group.passRate);
  return (
    <Link
      to="/results/session/$sessionId"
      params={{ sessionId: group.sessionId }}
      className="panel group flex flex-col gap-4 p-5 transition-colors hover:border-primary/50 hover:bg-secondary/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="hall-heading truncate text-base font-bold">
            {group.title}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {new Date(group.lastGraded).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </p>
        </div>
        <Badge variant={pass >= 50 ? "default" : "destructive"}>
          {pass}% pass
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Candidates" value={String(group.count)} />
        <Stat label="Passed" value={`${group.passed}/${group.count}`} />
        <Stat label="Avg score" value={`${group.avgScore.toFixed(0)}%`} />
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
        <div
          className={pass >= 50 ? "h-full bg-primary" : "h-full bg-destructive"}
          style={{ width: `${pass}%` }}
        />
      </div>

      <span className="mt-auto text-xs font-medium text-muted-foreground group-hover:text-foreground">
        View results →
      </span>
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-lg font-bold tabular-nums">{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}
